import React from "react";
import { Document, Page, Text, View, StyleSheet } from "@react-pdf/renderer";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import type { GeneratedLesson } from "./types";

const styles = StyleSheet.create({
  page: {
    padding: 40,
    backgroundColor: "#FFFFFF",
    fontFamily: "Helvetica",
  },
  header: {
    marginBottom: 30,
    paddingBottom: 20,
    borderBottomWidth: 2,
    borderBottomColor: "#1967d2",
  },
  title: {
    fontSize: 24,
    fontWeight: "bold",
    color: "#1967d2",
    marginBottom: 8,
  },
  subtitle: {
    fontSize: 12,
    color: "#5f6368",
    marginBottom: 4,
  },
  section: {
    marginBottom: 25,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: "bold",
    color: "#1967d2",
    marginBottom: 10,
    paddingBottom: 4,
    borderBottomWidth: 1,
    borderBottomColor: "#e0e0e0",
  },
  objectiveItem: {
    marginBottom: 6,
    fontSize: 11,
    color: "#202124",
  },
  lessonPlanItem: {
    marginBottom: 15,
    padding: 12,
    backgroundColor: "#f8f9fa",
    borderRadius: 4,
  },
  lessonPlanTitle: {
    fontSize: 14,
    fontWeight: "bold",
    color: "#188038",
    marginBottom: 6,
  },
  lessonPlanDesc: {
    fontSize: 11,
    color: "#5f6368",
    marginBottom: 8,
  },
  activityList: {
    marginLeft: 10,
  },
  activityItem: {
    fontSize: 10,
    color: "#202124",
    marginBottom: 4,
  },
  materialsList: {
    marginLeft: 10,
  },
  materialItem: {
    fontSize: 10,
    color: "#202124",
    marginBottom: 4,
  },
  twoColumn: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 20,
  },
  column: {
    width: "48%",
  },
  infoBadge: {
    backgroundColor: "#e8f0fe",
    padding: 6,
    borderRadius: 4,
    marginBottom: 8,
    fontSize: 10,
    color: "#1967d2",
  },
  footer: {
    position: "absolute",
    bottom: 30,
    left: 40,
    right: 40,
    textAlign: "center",
    fontSize: 9,
    color: "#9aa0a6",
    borderTopWidth: 1,
    borderTopColor: "#e0e0e0",
    paddingTop: 10,
  },
  headerInfoRow: {
    fontSize: 11,
    color: "#5f6368",
    marginBottom: 3,
  },
  gridContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
    marginBottom: 15,
  },
  gridItem: {
    fontSize: 10,
    backgroundColor: "#f1f3f4",
    padding: 6,
    borderRadius: 4,
    color: "#5f6368",
    marginBottom: 4,
  },
});

interface LessonPDFProps {
  lesson: GeneratedLesson;
}

const LessonPDF: React.FC<LessonPDFProps> = ({ lesson }) => {
  const currentDate = format(new Date(), "dd 'de' MMMM 'de' yyyy", {
    locale: ptBR,
  });

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.header}>
          <Text style={styles.title}>{lesson.title}</Text>
          <Text style={styles.subtitle}>
            {lesson.subject} • {lesson.grade}
          </Text>
          <Text style={styles.headerInfoRow}>
            Duração: {lesson.duration} minutos • Gerado em: {currentDate}
          </Text>
          <Text style={styles.headerInfoRow}>Tipo: {lesson.lessonType}</Text>
          <Text style={styles.headerInfoRow}>
            Dificuldade: {lesson.difficulty}
          </Text>
          <Text style={styles.headerInfoRow}>Tópico: {lesson.topic}</Text>
          {lesson.estimatedPreparationTime && (
            <Text style={styles.headerInfoRow}>
              Tempo de preparo: {lesson.estimatedPreparationTime}
            </Text>
          )}
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>OBJETIVOS DE APRENDIZADO</Text>
          {lesson.objectives.map((objective: string, index: number) => (
            <View key={index} style={{ flexDirection: "row", marginBottom: 6 }}>
              <Text style={{ fontSize: 11, marginRight: 8 }}>•</Text>
              <Text style={styles.objectiveItem}>{objective}</Text>
            </View>
          ))}
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>PLANO DE AULA</Text>
          {Object.values(lesson.lessonPlan).map(
            (section: any, index: number) => (
              <View key={index} style={styles.lessonPlanItem}>
                <Text style={styles.lessonPlanTitle}>{section.title}</Text>
                <Text style={styles.lessonPlanDesc}>{section.description}</Text>

                <Text
                  style={{
                    fontSize: 10,
                    fontWeight: "bold",
                    marginBottom: 4,
                  }}
                >
                  Atividades:
                </Text>
                <View style={styles.activityList}>
                  {section.activities.map((activity: string, i: number) => (
                    <View
                      key={i}
                      style={{ flexDirection: "row", marginBottom: 3 }}
                    >
                      <Text style={{ fontSize: 10, marginRight: 6 }}>•</Text>
                      <Text style={styles.activityItem}>{activity}</Text>
                    </View>
                  ))}
                </View>
              </View>
            ),
          )}
        </View>

        <View style={styles.twoColumn}>
          <View style={styles.column}>
            <Text style={styles.sectionTitle}>MATERIAIS NECESSÁRIOS</Text>
            <View style={styles.materialsList}>
              {lesson.materials.map((material: string, index: number) => (
                <View
                  key={index}
                  style={{ flexDirection: "row", marginBottom: 4 }}
                >
                  <Text style={{ fontSize: 10, marginRight: 6 }}>•</Text>
                  <Text style={styles.materialItem}>{material}</Text>
                </View>
              ))}
            </View>
          </View>

          <View style={styles.column}>
            <Text style={styles.sectionTitle}>ATIVIDADES DETALHADAS</Text>
            {lesson.activities
              .slice(0, 4)
              .map((activity: any, index: number) => (
                <View key={index} style={{ marginBottom: 10 }}>
                  <Text
                    style={{
                      fontSize: 11,
                      fontWeight: "bold",
                      marginBottom: 2,
                    }}
                  >
                    {activity.title} ({activity.duration} min)
                  </Text>
                  <Text style={{ fontSize: 10, color: "#5f6368" }}>
                    {activity.description}
                  </Text>
                </View>
              ))}
          </View>
        </View>

        {lesson.techResources && lesson.techResources.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>RECURSOS TECNOLÓGICOS</Text>
            <View style={styles.gridContainer}>
              {lesson.techResources.map((resource: string, index: number) => (
                <Text key={index} style={styles.gridItem}>
                  {resource}
                </Text>
              ))}
            </View>
          </View>
        )}

        {lesson.bnccInfo && lesson.bnccInfo.habilidades.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>HABILIDADES BNCC</Text>
            {lesson.bnccInfo.habilidades
              .slice(0, 5)
              .map((hab: string, index: number) => (
                <View
                  key={index}
                  style={{ flexDirection: "row", marginBottom: 4 }}
                >
                  <Text style={{ fontSize: 10, marginRight: 6 }}>•</Text>
                  <Text style={{ fontSize: 10, color: "#202124" }}>{hab}</Text>
                </View>
              ))}
          </View>
        )}

        <View style={styles.twoColumn}>
          <View style={styles.column}>
            <Text style={styles.sectionTitle}>
              PARA ALUNOS COM DIFICULDADES
            </Text>
            {lesson.differentiation?.forStruggling
              .slice(0, 3)
              .map((item: string, index: number) => (
                <View
                  key={index}
                  style={{ flexDirection: "row", marginBottom: 4 }}
                >
                  <Text style={{ fontSize: 10, marginRight: 6 }}>•</Text>
                  <Text style={{ fontSize: 10, color: "#202124" }}>{item}</Text>
                </View>
              ))}
          </View>

          <View style={styles.column}>
            <Text style={styles.sectionTitle}>PARA ALUNOS AVANÇADOS</Text>
            {lesson.differentiation?.forAdvanced
              .slice(0, 3)
              .map((item: string, index: number) => (
                <View
                  key={index}
                  style={{ flexDirection: "row", marginBottom: 4 }}
                >
                  <Text style={{ fontSize: 10, marginRight: 6 }}>•</Text>
                  <Text style={{ fontSize: 10, color: "#202124" }}>{item}</Text>
                </View>
              ))}
          </View>
        </View>

        <View style={styles.footer}>
          <Text>
            Gerado por SIGA • Aula criada com Inteligência Artificial • Para
            uso educacional
          </Text>
        </View>
      </Page>
    </Document>
  );
};

export default LessonPDF;
